import { useCallback, useState } from 'react';

import { useQuery } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { RelativityWorkspacesResponse } from '@/api/types/backend-dto-types';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface UseRelativityWorkspaceSearchOptions {
  enabled?: boolean;
  pageSize?: number;
}

export function useRelativityWorkspaceSearch(options: UseRelativityWorkspaceSearchOptions = {}) {
  const { enabled = true, pageSize = 10 } = options;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQueryState] = useState('');
  const { fetchConfigGET } = useGetFetchConfig();

  const fetchWorkspaces = async (): Promise<RelativityWorkspacesResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', currentPage.toString());
    searchParams.append('pageSize', pageSize.toString());
    if (searchQuery.trim()) {
      searchParams.append('search', searchQuery.trim());
    }

    const url = `${APIBaseChronos}/api/relativity/workspaces?${searchParams.toString()}`;
    const response = await fetch(url, fetchConfigGET);

    if (!response.ok) {
      throw new Error(`Failed to fetch Relativity workspaces: ${response.status} ${response.statusText}`);
    }

    return response.json();
  };

  const query = useQuery(
    [QueryEntity.RelativityWorkspaces, { page: currentPage, pageSize, search: searchQuery }],
    fetchWorkspaces,
    {
      enabled,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: (failureCount, error) => {
        if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
          return false;
        }
        return failureCount < 2;
      },
    },
  );

  const totalPages = query.data?.totalPages ?? 0;
  const canPrevious = currentPage > 1;
  const canNext = currentPage < totalPages;

  const setSearchQuery = useCallback((value: string) => {
    setSearchQueryState(value);
    setCurrentPage(1);
  }, []);

  const reset = useCallback(() => {
    setSearchQueryState('');
    setCurrentPage(1);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((page) => Math.max(1, page - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((page) => (totalPages > 0 ? Math.min(totalPages, page + 1) : page + 1));
  }, [totalPages]);

  return {
    ...query,
    workspacesData: query.data,
    workspaces: query.data?.workspaces ?? [],
    currentPage,
    searchQuery,
    totalPages,
    canPrevious,
    canNext,
    setCurrentPage,
    setSearchQuery,
    goToPreviousPage,
    goToNextPage,
    reset,
  };
}
